// Foreign merchant vessels (`sea.trade`) — the trading houses beyond the map.
//
// One hull for all three houses. The deck and the sail are *team* voxels, so
// the renderer paints them in the house's colour (the client's per-unit
// faction override) and keeps the grey shading the author wrote; the hull and
// mast stay dark wood. That matters for two reasons: a merchant must not read
// as another player's fleet (`playerColor(251)` is player 3's green), and the
// house has to be identifiable at a glance from a board seen from above.
//
// The three colours are deliberately outside the player palette (blue, orange,
// red, green, purple, yellow, cyan, pink): bone, old gold, cold steel.

import type { BlockVoxel, CharacterPart, CharacterPrefab } from './types.js';

/** The prefab id a merchant vessel renders as. */
export const MERCHANT_SHIP_PREFAB_ID = 'merchant_ship';

function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  team = false,
): BlockVoxel[] {
  const out: BlockVoxel[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++) {
        out.push(team ? { x, y, z, color, team: true } : { x, y, z, color });
      }
  return out;
}

/** Dark sea-wood — every merchant hull is the same, so a house reads by its paint. */
const HULL = '#3f3a31';
/** Mast and spars. */
const TIMBER = '#5a4632';
/** Cargo crates on deck: a trader, not a warship. */
const CRATE = '#7a6248';
/** Team voxels are authored in greys — the renderer swaps the hue for the house colour. */
const DECK_GREY = '#b0b0b0';
const SAIL_GREY = '#e4e4e4';

const merchantShip: CharacterPrefab = {
  name: 'Merchant Vessel',
  maxHp: 60,
  parts: merchantHullParts(),
};

/**
 * A single-masted coastal trader: a blunt dark hull, a painted deck, a tall
 * sail and a crate or two. The proportions follow the player's `trade_ship`
 * so the two read as the same class of vessel from a distance.
 */
function merchantHullParts(): CharacterPart[] {
  const w = 6;
  const d = 4;
  const h = 4;
  const deckY = Math.ceil(h * 0.4);
  const mastY = deckY + 1;
  return [
    // Hull.
    {
      name: 'hull',
      voxels: box(-Math.floor(w / 2), 0, -Math.floor(d / 2), w, deckY, d, HULL),
    },
    // Deck — painted in the house colour, so the board reads from above.
    {
      name: 'deck',
      voxels: box(
        -Math.floor(w * 0.4),
        deckY,
        -Math.floor(d * 0.3),
        Math.ceil(w * 0.8),
        1,
        Math.ceil(d * 0.6),
        DECK_GREY,
        true,
      ),
    },
    // Crates, lashed on deck.
    {
      name: 'cargo',
      voxels: [
        { x: -1, y: deckY + 1, z: -1, color: CRATE },
        { x: 1, y: deckY + 1, z: 1, color: CRATE },
      ],
    },
    // Mast.
    {
      name: 'mast',
      voxels: [
        { x: 0, y: mastY, z: 0, color: TIMBER },
        { x: 0, y: mastY + 1, z: 0, color: TIMBER },
        { x: 0, y: mastY + 2, z: 0, color: TIMBER },
        { x: 0, y: mastY + 3, z: 0, color: TIMBER },
      ],
    },
    // Sail — the house's colours, from the side.
    {
      name: 'sail',
      voxels: box(-1, mastY, 0, 3, 3, 1, SAIL_GREY, true),
    },
  ];
}

/** The merchant prefabs, by id. */
export const MERCHANT_PREFABS: Readonly<Record<string, CharacterPrefab>> = {
  [MERCHANT_SHIP_PREFAB_ID]: merchantShip,
};

/**
 * The three houses, in the sim's order (`CTradeVessel.house`): Salt, Amber,
 * Iron. The name lives in the client's i18n; the colour lives here, next to
 * the hull it paints.
 */
export const MERCHANT_HOUSE_COLORS = [
  '#e6e0d2', // Salt — bone white, the brightest sail on a dark sea.
  '#b8860b', // Amber — old gold, deeper than any player's yellow.
  '#7d90a6', // Iron — cold steel, greyer than any player's blue.
] as const satisfies readonly string[];
